/**
 * Crossmint client bridge for poker SPA.
 *
 * Best practice (Crossmint production docs):
 * - Player custodial UX + production **client** API key live on
 *   https://wallet.grudge-studio.com (not a second Crossmint app here).
 * - Optional: set VITE_CROSSMINT_CLIENT_API_KEY to enable embedded SDK later
 *   with Grudge ID JWT as BYO auth (console JWT auth must match).
 * - Server API keys stay on Railway; this module never embeds secrets.
 *
 * Heavy package `@crossmint/client-sdk-react-ui` is optional (native peer
 * conflicts on Windows installs). Prefer fleet REST + wallet site.
 */
import { CROSSMINT_FLEET, FLEET, GBUX } from "@shared/fleet";
import { authHeaders, getGrudgeToken, isLoggedIn } from "./grudge-auth";
import { WEB3 } from "./web3-ssot";

export const CROSSMINT_PACKAGES = {
  clientReact: "@crossmint/client-sdk-react-ui",
  walletsSdk: "@crossmint/wallets-sdk",
  /** Installed on this repo for Node server helpers — not bundled into SPA UI */
  installed: "@crossmint/wallets-sdk",
  restApiVersion: "2025-06-09",
  chain: "solana",
  network: "mainnet-beta",
  installNote:
    "wallets-sdk on poker for Railway/Node (server/services/crossmint-wallets.ts). React UI SDK on wallet.grudge-studio.com only. Edge: fleet REST + /api/wallet/scopes.",
} as const;

export function getCrossmintClientApiKey(): string | null {
  const k = (import.meta as { env?: { VITE_CROSSMINT_CLIENT_API_KEY?: string } })
    .env?.VITE_CROSSMINT_CLIENT_API_KEY;
  if (!k || /sk_|server/i.test(k)) return null; // refuse server-looking keys
  return k.trim() || null;
}

/** Open production Crossmint player product with optional return path */
export function openCrossmintWallet(returnPath = "/account"): void {
  const base = CROSSMINT_FLEET.walletSite || FLEET.walletSite;
  const ret = `${FLEET.publicOrigin}${returnPath.startsWith("/") ? returnPath : `/${returnPath}`}`;
  const url = `${base}${base.includes("?") ? "&" : "?"}returnTo=${encodeURIComponent(ret)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

export interface UnifiedWalletSnapshot {
  network: string;
  playGbux: number;
  accountGbux: number | null;
  external: { address: string; sol: number; gbuxOnChain: number } | null;
  custodial: {
    address: string | null;
    sol: number | null;
    gbuxOnChain: number | null;
    site: string;
  };
  mint: string;
  crossmintEnabled: boolean;
  sources?: string[];
}

/**
 * Single client call for all balance layers (play + fleet + external + custodial chain).
 */
export async function fetchUnifiedWalletSnapshot(
  walletAddress: string,
): Promise<UnifiedWalletSnapshot> {
  const headers = authHeaders({
    "X-Wallet-Address": walletAddress,
  });
  // Prefer scopes (edge SSOT; always registered). unified is alias when not proxied.
  const r = await fetch(
    `/api/wallet/scopes?wallet=${encodeURIComponent(walletAddress)}`,
    { headers },
  );
  if (!r.ok) {
    const s = await fetch(
      `/api/wallet/unified?wallet=${encodeURIComponent(walletAddress)}`,
      { headers },
    );
    const j = await s.json();
    return {
      network: "mainnet-beta",
      playGbux: Number(j.playWallet?.balance ?? 0) || 0,
      accountGbux: null,
      external: {
        address: walletAddress,
        sol: Number(j.connectedWallet?.sol ?? 0) || 0,
        gbuxOnChain: Number(j.connectedWallet?.gbuxOnChain ?? 0) || 0,
      },
      custodial: {
        address: null,
        sol: null,
        gbuxOnChain: null,
        site: CROSSMINT_FLEET.walletSite,
      },
      mint: GBUX.mint,
      crossmintEnabled: true,
    };
  }
  const j = await r.json();
  const u = j.unified || {};
  return {
    network: j.network || WEB3.network,
    playGbux: Number(j.playWallet?.balance ?? u.playGbux ?? 0) || 0,
    accountGbux:
      j.fleetBag?.gbux != null
        ? Number(j.fleetBag.gbux)
        : u.accountGbux != null
          ? Number(u.accountGbux)
          : null,
    external: {
      address: walletAddress,
      sol: Number(j.connectedWallet?.sol ?? u.external?.sol ?? 0) || 0,
      gbuxOnChain:
        Number(j.connectedWallet?.gbuxOnChain ?? u.external?.gbuxOnChain ?? 0) ||
        0,
    },
    custodial: {
      address: j.custodialWallet?.address ?? u.custodialWallet ?? null,
      sol:
        j.custodialWallet?.sol != null
          ? Number(j.custodialWallet.sol)
          : u.custodialOnChain?.sol ?? null,
      gbuxOnChain:
        j.custodialWallet?.gbuxOnChain != null
          ? Number(j.custodialWallet.gbuxOnChain)
          : u.custodialOnChain?.gbuxOnChain ?? null,
      site: CROSSMINT_FLEET.walletSite,
    },
    mint: j.realMint || GBUX.mint,
    crossmintEnabled: Boolean(j.crossmint?.enabled ?? true),
    sources: u.sources,
  };
}

export function hasGrudgeJwtForCrossmint(): boolean {
  return isLoggedIn() && Boolean(getGrudgeToken());
}
